import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  "Nouveau",
  "Appelé",
  "Soumission envoyée",
  "Gagné",
  "Perdu",
];

const TAB_OPTIONS = [
  { id: "overview", label: "Vue d'ensemble" },
  { id: "leads", label: "Leads" },
];

const PERIOD_OPTIONS = [
  { label: "7j", value: 7 },
  { label: "14j", value: 14 },
  { label: "28j", value: 28 },
  { label: "90j", value: 90 },
  { label: "Tout", value: "all" },
];

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedLead, setSelectedLead] = useState(null);
  const [formState, setFormState] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [periodFilter, setPeriodFilter] = useState(28);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [newLeadDialogOpen, setNewLeadDialogOpen] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresse_civique: "",
    ville: "",
    service: "",
    description: "",
    estimated_amount: "",
    status: "Nouveau",
    assigned_to: "",
    callback_date: "",
    internal_note: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  usePageMeta("Admin | Leads", "Tableau de bord des leads");

  const token = useMemo(() => localStorage.getItem("admin_token"), []);

  const fetchLeads = async () => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data);
    } catch (err) {
      setError("Impossible de charger les leads. Vérifiez votre connexion.");
    } finally {
      setLoading(false);
    }
  };

  const getLeadDate = (lead) => {
    const raw = lead.creation_date || lead.created_at;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  };

  const filteredLeads = useMemo(() => {
    let data = [...leads];
    if (periodFilter !== "all") {
      const days = Number(periodFilter);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);
      data = data.filter((lead) => {
        const date = getLeadDate(lead);
        return date && date >= cutoff;
      });
    }
    if (statusFilter !== "all") {
      data = data.filter((lead) => (lead.status || "Nouveau") === statusFilter);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter((lead) => {
        const values = [
          lead.prenom,
          lead.nom,
          lead.email,
          lead.telephone,
          lead.ville,
          lead.service,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return values.includes(query);
      });
    }
    return data;
  }, [leads, periodFilter, statusFilter, searchQuery]);

  const metrics = useMemo(() => {
    const total = filteredLeads.length;
    const byStatus = STATUS_OPTIONS.reduce((acc, status) => {
      acc[status] = filteredLeads.filter((lead) => (lead.status || "Nouveau") === status).length;
      return acc;
    }, {});
    const conversion = total ? Math.round((byStatus["Gagné"] / total) * 100) : 0;
    return [
      { label: "Total leads", value: total },
      { label: "Nouveaux", value: byStatus["Nouveau"] },
      { label: "Appelés", value: byStatus["Appelé"] },
      { label: "Soumissions", value: byStatus["Soumission envoyée"] },
      { label: "Gagnés", value: byStatus["Gagné"] },
      { label: "Conversion", value: `${conversion}%` },
    ];
  }, [filteredLeads]);

  const exportCsv = () => {
    const headers = [
      "Date",
      "Prénom",
      "Nom",
      "Email",
      "Téléphone",
      "Service",
      "Ville",
      "Statut",
      "Assigné",
      "Rappel",
      "Montant",
      "Note",
    ];
    const rows = filteredLeads.map((lead) => [
      formatDate(getLeadDate(lead)),
      lead.prenom || "",
      lead.nom || "",
      lead.email || "",
      lead.telephone || "",
      lead.service || "",
      lead.ville || "",
      lead.status || "Nouveau",
      lead.assigned_to || "",
      formatDate(lead.callback_date),
      lead.estimated_amount || "",
      lead.internal_note || "",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleNewLeadChange = (field, value) => {
    setNewLeadForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitNewLead = async () => {
    setError("");
    if (!token) return;
    try {
      const payload = {
        ...newLeadForm,
        estimated_amount: newLeadForm.estimated_amount
          ? Number(newLeadForm.estimated_amount)
          : undefined,
        callback_date: newLeadForm.callback_date || undefined,
      };
      const response = await axios.post(`${API}/admin/leads`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((prev) => [response.data, ...prev]);
      setNewLeadDialogOpen(false);
      setNewLeadForm({
        prenom: "",
        nom: "",
        email: "",
        telephone: "",
        adresse_civique: "",
        ville: "",
        service: "",
        description: "",
        estimated_amount: "",
        status: "Nouveau",
        assigned_to: "",
        callback_date: "",
        internal_note: "",
      });
    } catch (err) {
      setError("Impossible de créer la soumission.");
    }
  };

  const downloadQuote = async (leadId) => {
    if (!token) return;
    const response = await fetch(`${API}/admin/leads/${leadId}/quote`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      setError("Impossible de générer la soumission.");
      return;
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `soumission-${leadId}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess("");
    if (!passwordForm.current || !passwordForm.next) {
      setPasswordError("Veuillez remplir tous les champs.");
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError("Les mots de passe ne correspondent pas.");
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(
        `${API}/admin/password`,
        {
          current_password: passwordForm.current,
          new_password: passwordForm.next,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPasswordSuccess("Mot de passe mis à jour.");
      setPasswordForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      setPasswordError("Impossible de changer le mot de passe.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const openEdit = (lead) => {
    setSelectedLead(lead);
    setFormState({
      status: lead.status || "Nouveau",
      assigned_to: lead.assigned_to || "",
      callback_date: lead.callback_date ? lead.callback_date.slice(0, 10) : "",
      internal_note: lead.internal_note || "",
      estimated_amount: lead.estimated_amount ?? "",
    });
  };

  const handleUpdate = async () => {
    if (!selectedLead) return;
    try {
      const payload = {
        ...formState,
        callback_date: formState.callback_date
          ? new Date(formState.callback_date).toISOString()
          : null,
        estimated_amount:
          formState.estimated_amount === ""
            ? null
            : Number(formState.estimated_amount),
      };
      const { data } = await axios.patch(`${API}/admin/leads/${selectedLead.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((prev) => prev.map((lead) => (lead.id === data.id ? data : lead)));
      setSelectedLead(null);
    } catch (err) {
      setError("Impossible de mettre à jour ce lead.");
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("fr-CA");
  };

  const displayedLeads = activeTab === "overview" ? filteredLeads.slice(0, 6) : filteredLeads;

  return (
    <div className="min-h-screen bg-[#0b1020] text-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-secondary" data-testid="admin-dashboard-eyebrow">
              Tableau interne
            </p>
            <h1 className="text-2xl font-bold text-white" data-testid="admin-dashboard-title">
              Suivi des leads pavé uni
            </h1>
            <p className="text-sm text-slate-300" data-testid="admin-dashboard-subtitle">
              Vue centralisée pour le suivi des demandes et conversions.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={fetchLeads}
              data-testid="admin-refresh-button"
            >
              Rafraîchir
            </Button>
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={() => setNewLeadDialogOpen(true)}
              data-testid="admin-new-lead-button"
            >
              Nouvelle soumission
            </Button>
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={exportCsv}
              data-testid="admin-export-button"
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={() => navigate("/admin/seo")}
              data-testid="admin-seo-button"
            >
              SEO IA
            </Button>
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold border-white/30 text-white hover:bg-white/10"
              onClick={() => setPasswordDialogOpen(true)}
              data-testid="admin-password-button"
            >
              Changer mot de passe
            </Button>
            <Button
              className="rounded-none uppercase tracking-wider font-bold bg-white text-slate-900 hover:bg-slate-200"
              onClick={handleLogout}
              data-testid="admin-logout-button"
            >
              Déconnexion
            </Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pb-6">
          <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
            {TAB_OPTIONS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.3em] rounded-full border transition ${
                  activeTab === tab.id
                    ? "bg-secondary text-slate-950 border-secondary"
                    : "border-white/20 text-slate-300 hover:text-white"
                }`}
                data-testid={`admin-tab-${tab.id}`}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-testid="admin-filters">
          <div className="flex flex-wrap gap-2" data-testid="admin-period-filter">
            {PERIOD_OPTIONS.map((option) => (
              <button
                key={option.label}
                onClick={() => setPeriodFilter(option.value)}
                className={`px-3 py-1 text-xs rounded-full border transition ${
                  periodFilter === option.value
                    ? "bg-white text-slate-900 border-white"
                    : "border-white/20 text-slate-300 hover:text-white"
                }`}
                data-testid={`admin-period-${option.label}`}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Rechercher un lead..."
              className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 w-56"
              data-testid="admin-search-input"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="bg-white/10 border-white/20 text-white w-48"
                data-testid="admin-status-filter"
              >
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="admin-status-all">
                  Tous les statuts
                </SelectItem>
                {STATUS_OPTIONS.map((statusOption) => (
                  <SelectItem
                    key={statusOption}
                    value={statusOption}
                    data-testid={`admin-status-${statusOption}`}
                  >
                    {statusOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8" data-testid="admin-metrics">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="rounded-xl border border-white/10 bg-white/5 px-5 py-4"
              data-testid={`admin-metric-${metric.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{metric.label}</p>
              <p className="text-2xl font-semibold text-white mt-3">{metric.value}</p>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-6 text-sm text-red-400" data-testid="admin-dashboard-error">
            {error}
          </div>
        )}
        {loading ? (
          <p data-testid="admin-dashboard-loading">Chargement...</p>
        ) : displayedLeads.length === 0 ? (
          <p data-testid="admin-dashboard-empty">Aucun lead pour le moment.</p>
        ) : (
          <>
            <div className="hidden lg:block" data-testid="admin-leads-table">
              <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-300">Date</TableHead>
                      <TableHead className="text-slate-300">Nom</TableHead>
                      <TableHead className="text-slate-300">Téléphone</TableHead>
                      <TableHead className="text-slate-300">Service</TableHead>
                      <TableHead className="text-slate-300">Ville</TableHead>
                      <TableHead className="text-slate-300">Statut</TableHead>
                      <TableHead className="text-slate-300">Assigné</TableHead>
                      <TableHead className="text-slate-300">Rappel</TableHead>
                      <TableHead className="text-slate-300">Estimé</TableHead>
                      <TableHead className="text-slate-300">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedLeads.map((lead) => (
                      <TableRow key={lead.id} className="border-white/10" data-testid={`admin-lead-row-${lead.id}`}>
                        <TableCell data-testid={`admin-lead-${lead.id}-date`}>
                          {formatDate(getLeadDate(lead))}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-name`}>
                          {[lead.prenom, lead.nom].filter(Boolean).join(" ") || "-"}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-phone`}>
                          {lead.telephone || "-"}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-service`}>
                          {lead.service || "-"}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-city`}>
                          {lead.ville || "-"}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-status`}>
                          {lead.status || "Nouveau"}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-assigned`}>
                          {lead.assigned_to || "-"}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-callback`}>
                          {formatDate(lead.callback_date)}
                        </TableCell>
                        <TableCell data-testid={`admin-lead-${lead.id}-amount`}>
                          {lead.estimated_amount ? `${lead.estimated_amount} $` : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              className="rounded-none uppercase tracking-wider font-bold text-xs border-white/40 text-white hover:bg-white/10"
                              onClick={() => openEdit(lead)}
                              data-testid={`admin-lead-${lead.id}-edit`}
                            >
                              Modifier
                            </Button>
                            <Button
                              variant="outline"
                              className="rounded-none uppercase tracking-wider font-bold text-xs border-white/40 text-white hover:bg-white/10"
                              onClick={() => downloadQuote(lead.id)}
                              data-testid={`admin-lead-${lead.id}-quote`}
                            >
                              Soumission
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="lg:hidden space-y-4" data-testid="admin-leads-cards">
              {displayedLeads.map((lead) => (
                <div
                  key={lead.id}
                  className="border border-white/10 bg-white/5 p-4 shadow-sm space-y-2 rounded-xl"
                  data-testid={`admin-lead-card-${lead.id}`}
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                    {formatDate(getLeadDate(lead))}
                  </p>
                  <p className="font-semibold text-white" data-testid={`admin-lead-card-${lead.id}-name`}>
                    {[lead.prenom, lead.nom].filter(Boolean).join(" ") || "-"}
                  </p>
                  <p className="text-sm text-slate-300" data-testid={`admin-lead-card-${lead.id}-info`}>
                    {lead.service || "-"} · {lead.ville || "-"}
                  </p>
                  <p className="text-sm" data-testid={`admin-lead-card-${lead.id}-phone`}>
                    {lead.telephone || "-"}
                  </p>
                  <Button
                    variant="outline"
                    className="rounded-none uppercase tracking-wider font-bold text-xs border-white/40 text-white hover:bg-white/10"
                    onClick={() => openEdit(lead)}
                    data-testid={`admin-lead-card-${lead.id}-edit`}
                  >
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    className="rounded-none uppercase tracking-wider font-bold text-xs border-white/40 text-white hover:bg-white/10"
                    onClick={() => downloadQuote(lead.id)}
                    data-testid={`admin-lead-card-${lead.id}-quote`}
                  >
                    Soumission
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <Dialog open={Boolean(selectedLead)} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-xl" data-testid="admin-lead-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-lead-dialog-title">Mettre à jour le lead</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold" data-testid="admin-lead-status-label">
                  Statut
                </label>
                <Select
                  value={formState.status}
                  onValueChange={(value) => setFormState((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger data-testid="admin-lead-status-select">
                    <SelectValue placeholder="Choisir" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((statusOption) => (
                      <SelectItem
                        key={statusOption}
                        value={statusOption}
                        data-testid={`admin-lead-status-option-${statusOption}`}
                      >
                        {statusOption}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold" data-testid="admin-lead-assigned-label">
                  Assigné à
                </label>
                <Input
                  value={formState.assigned_to}
                  onChange={(event) => setFormState((prev) => ({ ...prev, assigned_to: event.target.value }))}
                  placeholder="Nom de l'employé"
                  data-testid="admin-lead-assigned-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold" data-testid="admin-lead-callback-label">
                  Date de rappel
                </label>
                <Input
                  type="date"
                  value={formState.callback_date}
                  onChange={(event) => setFormState((prev) => ({ ...prev, callback_date: event.target.value }))}
                  data-testid="admin-lead-callback-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold" data-testid="admin-lead-amount-label">
                  Montant estimé
                </label>
                <Input
                  type="number"
                  value={formState.estimated_amount}
                  onChange={(event) => setFormState((prev) => ({ ...prev, estimated_amount: event.target.value }))}
                  placeholder="0"
                  data-testid="admin-lead-amount-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold" data-testid="admin-lead-note-label">
                  Note interne
                </label>
                <Textarea
                  value={formState.internal_note}
                  onChange={(event) => setFormState((prev) => ({ ...prev, internal_note: event.target.value }))}
                  rows={4}
                  data-testid="admin-lead-note-input"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  className="rounded-none uppercase tracking-wider font-bold"
                  onClick={() => setSelectedLead(null)}
                  data-testid="admin-lead-cancel"
                >
                  Annuler
                </Button>
                <Button
                  className="rounded-none uppercase tracking-wider font-bold"
                  onClick={handleUpdate}
                  data-testid="admin-lead-save"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newLeadDialogOpen} onOpenChange={setNewLeadDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="admin-new-lead-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-new-lead-title">Nouvelle soumission</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-prenom-label">
                Prénom
              </label>
              <Input
                value={newLeadForm.prenom}
                onChange={(event) => handleNewLeadChange("prenom", event.target.value)}
                data-testid="admin-new-lead-prenom-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-nom-label">
                Nom
              </label>
              <Input
                value={newLeadForm.nom}
                onChange={(event) => handleNewLeadChange("nom", event.target.value)}
                data-testid="admin-new-lead-nom-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-telephone-label">
                Téléphone
              </label>
              <Input
                value={newLeadForm.telephone}
                onChange={(event) => handleNewLeadChange("telephone", event.target.value)}
                data-testid="admin-new-lead-telephone-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-email-label">
                Courriel
              </label>
              <Input
                value={newLeadForm.email}
                onChange={(event) => handleNewLeadChange("email", event.target.value)}
                data-testid="admin-new-lead-email-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-adresse-label">
                Adresse civique
              </label>
              <Input
                value={newLeadForm.adresse_civique}
                onChange={(event) => handleNewLeadChange("adresse_civique", event.target.value)}
                data-testid="admin-new-lead-adresse-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-ville-label">
                Ville
              </label>
              <Input
                value={newLeadForm.ville}
                onChange={(event) => handleNewLeadChange("ville", event.target.value)}
                data-testid="admin-new-lead-ville-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-service-label">
                Service
              </label>
              <Input
                value={newLeadForm.service}
                onChange={(event) => handleNewLeadChange("service", event.target.value)}
                data-testid="admin-new-lead-service-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-description-label">
                Description
              </label>
              <Textarea
                value={newLeadForm.description}
                onChange={(event) => handleNewLeadChange("description", event.target.value)}
                rows={4}
                data-testid="admin-new-lead-description-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-amount-label">
                Montant estimé
              </label>
              <Input
                type="number"
                value={newLeadForm.estimated_amount}
                onChange={(event) => handleNewLeadChange("estimated_amount", event.target.value)}
                data-testid="admin-new-lead-amount-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-status-label">
                Statut
              </label>
              <Select
                value={newLeadForm.status}
                onValueChange={(value) => handleNewLeadChange("status", value)}
              >
                <SelectTrigger data-testid="admin-new-lead-status-select">
                  <SelectValue placeholder="Choisir" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((statusOption) => (
                    <SelectItem
                      key={statusOption}
                      value={statusOption}
                      data-testid={`admin-new-lead-status-option-${statusOption}`}
                    >
                      {statusOption}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-assigned-label">
                Assigné à
              </label>
              <Input
                value={newLeadForm.assigned_to}
                onChange={(event) => handleNewLeadChange("assigned_to", event.target.value)}
                data-testid="admin-new-lead-assigned-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-callback-label">
                Date de rappel
              </label>
              <Input
                type="date"
                value={newLeadForm.callback_date}
                onChange={(event) => handleNewLeadChange("callback_date", event.target.value)}
                data-testid="admin-new-lead-callback-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-semibold" data-testid="admin-new-lead-note-label">
                Note interne
              </label>
              <Textarea
                value={newLeadForm.internal_note}
                onChange={(event) => handleNewLeadChange("internal_note", event.target.value)}
                rows={3}
                data-testid="admin-new-lead-note-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              className="rounded-none uppercase tracking-wider font-bold"
              onClick={() => setNewLeadDialogOpen(false)}
              data-testid="admin-new-lead-cancel"
            >
              Annuler
            </Button>
            <Button
              className="rounded-none uppercase tracking-wider font-bold"
              onClick={submitNewLead}
              data-testid="admin-new-lead-save"
            >
              Créer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-password-dialog">
          <DialogHeader>
            <DialogTitle data-testid="admin-password-title">Changer le mot de passe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-password-current-label">
                Mot de passe actuel
              </label>
              <Input
                type="password"
                value={passwordForm.current}
                onChange={(event) =>
                  setPasswordForm((prev) => ({ ...prev, current: event.target.value }))
                }
                data-testid="admin-password-current-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-password-new-label">
                Nouveau mot de passe
              </label>
              <Input
                type="password"
                value={passwordForm.next}
                onChange={(event) =>
                  setPasswordForm((prev) => ({ ...prev, next: event.target.value }))
                }
                data-testid="admin-password-new-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold" data-testid="admin-password-confirm-label">
                Confirmer le mot de passe
              </label>
              <Input
                type="password"
                value={passwordForm.confirm}
                onChange={(event) =>
                  setPasswordForm((prev) => ({ ...prev, confirm: event.target.value }))
                }
                data-testid="admin-password-confirm-input"
              />
            </div>
            {passwordError && (
              <p className="text-sm text-red-600" data-testid="admin-password-error">
                {passwordError}
              </p>
            )}
            {passwordSuccess && (
              <p className="text-sm text-emerald-600" data-testid="admin-password-success">
                {passwordSuccess}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                className="rounded-none uppercase tracking-wider font-bold"
                onClick={() => setPasswordDialogOpen(false)}
                data-testid="admin-password-cancel"
              >
                Fermer
              </Button>
              <Button
                className="rounded-none uppercase tracking-wider font-bold"
                onClick={handlePasswordChange}
                disabled={passwordLoading}
                data-testid="admin-password-save"
              >
                {passwordLoading ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;